import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  TextField,
  Button,
} from '@mui/material';
import { useApiGet, useApiSend } from '../../utils/httpClient';
import {
  getUserPI,
  updateUserPI,
  createUserAddress,
  removeUserAddress,
} from '../../endpoints/users';
import { getPostalInfo } from '../../endpoints/lookups';
import { Add, Cancel, Delete, Edit, Save } from '@mui/icons-material';
import dayjs from 'dayjs';
import ValidatedTextField from '../../Components/ValidatedTextField';
import { emailValidator, phoneNumberValidator } from '../../utils/validators';
import StateSelector from '../../Components/StateSelector';
import CountrySelector from '../../Components/CountrySelector';
import DateFilter from '../../Components/DateFilter';
import { addressTypes, roles } from '../../utils/variables';
import ConfirmationDialog from '../../Components/ConfirmationDialog';

export default function UserPI({
  id,
  refetchParent,
  canEdit,
  canAction,
  needToRefetch,
  updateNeedToRefetch,
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [errorSaving, setErrorSaving] = useState(null);

  // Personal Information
  const [editingPI, setEditingPI] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [homePhone, setHomePhone] = useState('');
  const [cellPhone, setCellPhone] = useState('');

  // Address
  const [editingAddress, setEditingAddress] = useState(false);
  const [address1, setAddress1] = useState('');
  const [address1Error, setAddress1Error] = useState(false);
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [cityError, setCityError] = useState(false);
  const [state, setState] = useState('');
  const [stateError, setStateError] = useState(false);
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  const [countryError, setCountryError] = useState(false);
  const [addressType, setAddressType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startDateError, setStartDateError] = useState(false);
  const [endDate, setEndDate] = useState('');
  const [endDateError, setEndDateError] = useState(false);
  const [addressId, setAddressId] = useState(null);
  const [delayPostalCode, setDelayPostalCode] = useState('');
  const [getPostalInfoActive, setGetPostalInfoActive] = useState(false);
  const [postalCodeError, setPostalCodeError] = useState(false);
  const [loadedAddress, setLoadedAddress] = useState(false);
  const [deathDate, setDeathDate] = useState(null);

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [addressToRemove, setAddressToRemove] = useState(null);

  const resetPersonalInfo = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setHomePhone('');
    setCellPhone('');
    setEditingPI(false);
  };
  const resetAddress = () => {
    setAddress1('');
    setAddress2('');
    setCity('');
    setState('');
    setPostalCode('');
    setCountry('');
    setAddressType('');
    setStartDate('');
    setEndDate('');
    setAddressId(null);
    setEditingAddress(false);
    setPostalCodeError(false);
    setDelayPostalCode('');
    setGetPostalInfoActive(false);
    setLoadedAddress(false);
  };

  const resetState = () => {
    setIsSaving(false);
    setErrorSaving(null);
    resetPersonalInfo();
    resetAddress();
  };

  const {
    data: userInfo,
    isLoading,
    refetch,
  } = useApiGet(
    ['userInfo', id],
    getUserPI,
    {
      enabled: !!id,
    },
    { id }
  );

  const { mutate: updateUserInfo } = useApiSend(
    updateUserPI,
    (data) => {
      resetState();
      refetch();
      refetchParent();
      setIsSaving(false);
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: addUserAddress } = useApiSend(
    createUserAddress,
    (data) => {
      resetState();
      refetch();
      refetchParent();
      setIsSaving(false);
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: removeAddress } = useApiSend(
    removeUserAddress,
    (data) => {
      resetState();
      refetch();
      refetchParent();
      setIsSaving(false);
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { data: postalInfo } = useApiGet(
    ['postal-info', postalCode, country],
    getPostalInfo,
    {
      enabled: getPostalInfoActive,
    },
    { postalCode, countryId: country }
  );

  useEffect(() => {
    if (
      postalCode &&
      postalCode.length > 4 &&
      country &&
      country.length > 0 &&
      !loadedAddress
    ) {
      setGetPostalInfoActive(true);
    } else {
      setGetPostalInfoActive(false);
    }
  }, [postalCode, country, loadedAddress]);

  useEffect(() => {
    if (postalInfo) {
      if (postalInfo.city) {
        setCity(postalInfo.city);
        if (postalInfo.city.length > 0) {
          setCityError(false);
        }
      }
      if (postalInfo.state_id) {
        setState(postalInfo.state_id);
        if (postalInfo.state_id.length > 0) {
          setStateError(false);
        }
      }
    }
  }, [postalInfo]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPostalCode(delayPostalCode);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delayPostalCode]);

  useEffect(() => {
    if (needToRefetch) {
      console.log('Refetching User PI');
      refetch();
      updateNeedToRefetch(false);
    }
  }, [needToRefetch, refetch, updateNeedToRefetch]);

  const handlePostalCodeChange = (event) => {
    setLoadedAddress(false);
    let value = event.target.value;
    setDelayPostalCode(value);
    if (value.length > 0) {
      setPostalCodeError(false);
    }
  };

  const handleEditPI = () => {
    setEditingPI(true);
    setFirstName(userInfo.first_name);
    setLastName(userInfo.last_name);
    setEmail(userInfo.email);
    setHomePhone(userInfo.home_phone);
    setCellPhone(userInfo.cell_phone);
    setDeathDate(userInfo.death_date ? dayjs(userInfo.death_date) : null);
  };

  const handleSavePI = () => {
    setIsSaving(true);
    const data = {
      id,
      first_name: firstName,
      last_name: lastName,
      email: email,
      home_phone: homePhone,
      cell_phone: cellPhone,
      death_date: deathDate ? deathDate.format('YYYY-MM-DD') : null,
    };
    updateUserInfo(data);
  };

  const handleEditAddress = (address) => {
    setEditingAddress(true);
    setAddress1(address.address_1);
    setAddress2(address.address_2);
    setCity(address.city);
    setState(address.state_id);
    setPostalCode(address.postal_code);
    setDelayPostalCode(address.postal_code);
    setCountry(address.country_id);
    setAddressType(address.address_type_id);
    setStartDate(address.start_date);
    setEndDate(address.end_date);
    setAddressId(address.id);
    setLoadedAddress(true);
  };

  const handleConfirmRemoveAddress = (address) => {
    setAddressToRemove(address);
    setShowConfirmationDialog(true);
  };

  const handleRemoveAddress = () => {
    removeAddress({ id, addressId: addressToRemove.id });
  };

  const handleCancelRemoveAddress = () => {
    setAddressToRemove(null);
    setShowConfirmationDialog(false);
  };

  const handleAddAddress = () => {
    setEditingAddress(true);
    if (userInfo.addresses.length > 0) {
      // need to loop through addresses to find which one is being added (can only have one of each type)
      let addressType = addressTypes.Address;
      for (let i = 0; i < userInfo.addresses.length; i++) {
        if (userInfo.addresses[i].address_type_id === addressType) {
          addressType = addressTypes.Seasonal;
        }
      }
      setAddressType(addressType);
    } else {
      setAddressType(addressTypes.Address);
    }
  };

  const handleSaveAddress = () => {
    setIsSaving(true);
    let valid = true;
    if (postalCode.length === 0) {
      setPostalCodeError(true);
      valid = false;
    }
    if (state.length === 0) {
      setStateError(true);
      valid = false;
    }
    if (city.length === 0) {
      setCityError(true);
      valid = false;
    }
    if (address1.length === 0) {
      setAddress1Error(true);
      valid = false;
    }
    if (country.length === 0) {
      setCountryError(true);
      valid = false;
    }
    if (addressType === addressTypes.Seasonal) {
      if (!startDate || !endDate) {
        setStartDateError(!startDate);
        setEndDateError(!endDate);
        valid = false;
      }
    }
    if (!valid) {
      setIsSaving(false);
      return;
    }
    let data = {
      id,
      address_1: address1,
      address_2: address2,
      city: city,
      state_id: state,
      postal_code: postalCode,
      country_id: country,
      address_type_id: addressType,
      start_date: startDate ? startDate.format('YYYY-MM-DD') : null,
      end_date: endDate ? endDate.format('YYYY-MM-DD') : null,
    };
    if (!addressId) {
      addUserAddress(data);
    } else {
      // Add the address_id to the data object
      data.address_id = addressId;
      updateUserInfo(data);
    }
  };

  const handleCountryChange = (value) => {
    if (value) {
      if (value.id !== country) {
        setState('');
      }
      setCountryError(false);
      setCountry(value.id);
      //setStateRequired(value.has_states);
    } else {
      setCountry('');
    }
  };

  const handleStartDateChange = (value) => {
    setStartDate(value);
    if (value) {
      setStartDateError(false);
    }
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    if (value) {
      setEndDateError(false);
    }
  };

  const handleAddress1Change = (value) => {
    setAddress1(value);
    if (value) {
      setAddress1Error(false);
    }
  };

  const handleStateChange = (value) => {
    if (value) {
      setState(value.id);
      setStateError(false);
    } else {
      setState('');
    }
  };

  const handleCityChange = (value) => {
    setCity(value);
    if (value) {
      setCityError(false);
    }
  };

  const handleRouteToSeeking = () => {
    window.location.href = `/seeking-transfer/create/${id}`;
  };

  // sort addresses by address type name so primary address comes first
  const sortedAddresses = userInfo?.addresses?.sort((a, b) => {
    return a.address_type
      .toLowerCase()
      .localeCompare(b.address_type.toLowerCase());
  });

  const isAdmin = () => {
    let role = localStorage.getItem('role');
    return role === roles.Admin;
  };

  if (isLoading) {
    return (
      <Grid container>
        <Grid item xs={12} padding textAlign={'center'}>
          <Typography variant="h4">Loading...</Typography>
        </Grid>
      </Grid>
    );
  }
  if (!userInfo) {
    return (
      <Grid container>
        <Grid item xs={12} padding>
          <Typography variant="h6">User not found</Typography>
        </Grid>
      </Grid>
    );
  }
  if (userInfo.is_partial) {
    return (
      <Grid container>
        <Grid item xs={12} padding>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader title="Personal Information" sx={{ p: 1 }} />
            <CardContent sx={{ p: 1 }}>
              <Grid container>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" component={'span'}>
                    <strong>First Name:</strong> {userInfo.first_name}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body1" component={'span'}>
                    <strong>Last Name:</strong> {userInfo.last_name}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body1" component={'span'}>
                    <strong>Is Member?:</strong>{' '}
                    {userInfo.is_member ? 'Yes' : 'No'}
                  </Typography>
                </Grid>

                {userInfo.is_member && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" component={'span'}>
                      <strong>Current/Last Chapter:</strong>{' '}
                      {userInfo.last_chapter}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" component={'span'}>
                    <strong>Member ID:</strong> {userInfo.imis_id || 'N/A'}
                  </Typography>
                </Grid>
                {userInfo.is_member && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" component={'span'}>
                      <strong>Active Member:</strong>{' '}
                      {userInfo.is_active ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  } else {
    return (
      <Grid container>
        {isAdmin() && (
          <>
            {canEdit && userInfo.is_member && !!!userInfo.death_date && (
              <Grid item xs={12} padding textAlign={'right'}>
                {userInfo.transfer_request_id ? (
                  <Button
                    variant="contained"
                    size={'small'}
                    color={'blue'}
                    sx={{ fontWeight: 'bold' }}
                    onClick={() =>
                      (window.location.href = `/seeking-transfer/${userInfo.transfer_request_id}`)
                    }
                  >
                    View Transfer
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size={'small'}
                    color={'blue'}
                    sx={{ fontWeight: 'bold' }}
                    onClick={handleRouteToSeeking}
                  >
                    Seek Transfer
                  </Button>
                )}
              </Grid>
            )}
          </>
        )}

        <Grid item xs={12} padding>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader title="Personal Information" sx={{ p: 1 }} />
            <CardContent sx={{ p: 1 }}>
              <Grid container>
                {editingPI ? (
                  <Grid item xs={12} md={6} padding>
                    <TextField
                      label="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      fullWidth
                      size="small"
                      margin="normal"
                      variant="outlined"
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" component={'span'}>
                      <strong>First Name:</strong> {userInfo.first_name}
                    </Typography>
                  </Grid>
                )}

                {editingPI ? (
                  <Grid item xs={12} md={6} padding>
                    <TextField
                      label="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      fullWidth
                      size="small"
                      margin="normal"
                      variant="outlined"
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" component={'span'}>
                      <strong>Last Name:</strong> {userInfo.last_name}
                    </Typography>
                  </Grid>
                )}
                {userInfo.home_phone !== undefined && (
                  <>
                    {editingPI ? (
                      <Grid item xs={12} md={6} padding>
                        <ValidatedTextField
                          label="Home Phone"
                          validator={phoneNumberValidator}
                          onChange={(error, value) => setHomePhone(value)}
                          value={homePhone}
                        />
                      </Grid>
                    ) : (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body1" component={'span'}>
                          <strong>Home Phone:</strong> {userInfo.home_phone}
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}
                {editingPI ? (
                  <Grid item xs={12} md={6} padding>
                    <ValidatedTextField
                      label="Cell Phone"
                      validator={phoneNumberValidator}
                      onChange={(error, value) => setCellPhone(value)}
                      value={cellPhone}
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" component={'span'}>
                      <strong>Cell Phone:</strong> {userInfo.cell_phone}
                    </Typography>
                  </Grid>
                )}
                {editingPI ? (
                  <Grid item xs={12} md={6} padding>
                    <ValidatedTextField
                      label="Email"
                      validator={emailValidator}
                      onChange={(error, value) => setEmail(value)}
                      value={email}
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" component={'span'}>
                      <strong>Email:</strong> {userInfo.email}
                    </Typography>
                  </Grid>
                )}
                {userInfo.death_date && !editingPI && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" component={'span'}>
                      <strong>Death Date:</strong>{' '}
                      {dayjs(userInfo.death_date).format('MMM D, YYYY')}
                    </Typography>
                  </Grid>
                )}
                {editingPI && canAction && (
                  <Grid item xs={12} md={6} padding>
                    <DateFilter
                      label="Death Date"
                      filterValue={deathDate}
                      updateFilters={setDeathDate}
                    />
                  </Grid>
                )}
                {!editingPI && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" component={'span'}>
                      <strong>Is Member?:</strong>{' '}
                      {userInfo.is_member ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                )}
                {!editingPI && userInfo.is_member && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" component={'span'}>
                      <strong>Current/Last Chapter:</strong>{' '}
                      {userInfo.last_chapter}
                    </Typography>
                  </Grid>
                )}
                {!editingPI && userInfo.is_member && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" component={'span'}>
                      <strong>Member ID:</strong> {userInfo.imis_id || 'N/A'}
                    </Typography>
                  </Grid>
                )}
                {!editingPI && userInfo.is_member && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" component={'span'}>
                      <strong>Active Member:</strong>{' '}
                      {userInfo.is_active ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
            {canEdit && (
              <CardActions>
                <Grid container item justifyContent="flex-end">
                  {editingPI ? (
                    <Grid item>
                      <Button
                        sx={{ marginRight: 1, fontWeight: 'bold' }}
                        variant="contained"
                        size={'small'}
                        color={'warning'}
                        onClick={resetPersonalInfo}
                        disabled={isSaving}
                      >
                        Cancel
                      </Button>
                      <Button
                        sx={{ marginRight: 1, fontWeight: 'bold' }}
                        variant="contained"
                        size={'small'}
                        onClick={handleSavePI}
                        disabled={isSaving}
                        color={'green'}
                      >
                        <Save sx={{ fontSize: 18, paddingRight: 1 }} />
                        {isSaving ? 'Saving...' : 'Save'}
                      </Button>
                    </Grid>
                  ) : (
                    <Grid item>
                      <Button
                        variant="contained"
                        color="blue"
                        size={'small'}
                        onClick={handleEditPI}
                        sx={{ marginRight: 1, fontWeight: 'bold' }}
                      >
                        <Edit sx={{ fontSize: 18, paddingRight: 1 }} />
                        Edit Personal Info
                      </Button>
                    </Grid>
                  )}
                  {errorSaving && editingPI && (
                    <Grid item xs={12}>
                      <Typography variant="body1" color={'error'}>
                        {errorSaving}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardActions>
            )}
          </Card>

          <Grid item container xs={12}>
            {canEdit && userInfo.addresses.length < 2 && (
              <Grid item xs={12} padding paddingTop={4} textAlign={'center'}>
                <Button
                  variant="contained"
                  size={'small'}
                  color={'green'}
                  onClick={() => handleAddAddress()}
                  disabled={isSaving}
                  sx={{ marginRight: 1, fontWeight: 'bold' }}
                >
                  <Add sx={{ fontSize: 18, paddingRight: 1 }} />
                  Add Address
                </Button>
              </Grid>
            )}

            {userInfo.addresses.length === 0 ? (
              <Grid item container xs={12}>
                <Grid item xs={12} padding textAlign="center" paddingTop={3}>
                  <Typography variant="body1">
                    No addresses available
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <Grid item container xs={12}>
                {!!!editingAddress && (
                  <Grid item container xs={12}>
                    {sortedAddresses.map((address) => (
                      <Grid item xs={12} md={6} key={address.id} padding>
                        <Card
                          sx={{
                            p: 2,
                            boxShadow: 3,
                            borderRadius: 2,
                            marginTop: 2,
                          }}
                        >
                          <CardHeader
                            title={
                              address.address_type.toLowerCase() === 'address'
                                ? 'Primary'
                                : address.address_type
                            }
                            sx={{ p: 1 }}
                          />
                          <CardContent sx={{ p: 1 }}>
                            <Grid container>
                              <Grid container item xs={12} padding>
                                <Grid item xs={12}>
                                  <Typography
                                    variant="body1"
                                    component={'span'}
                                  >
                                    <strong>Street:</strong> {address.address_1}
                                  </Typography>
                                </Grid>
                                {address.address_2 && (
                                  <Grid item xs={12}>
                                    <Typography
                                      variant="body1"
                                      component={'span'}
                                    >
                                      <strong>Street 2:</strong>{' '}
                                      {address.address_2}
                                    </Typography>
                                  </Grid>
                                )}
                                <Grid item xs={12}>
                                  <Typography
                                    variant="body1"
                                    component={'span'}
                                  >
                                    <strong>City:</strong> {address.city}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography
                                    variant="body1"
                                    component={'span'}
                                  >
                                    <strong>Postal Code:</strong>{' '}
                                    {address.postal_code}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography
                                    variant="body1"
                                    component={'span'}
                                  >
                                    <strong>{'State/Province:'}</strong>{' '}
                                    {address.state_abbreviation}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography
                                    variant="body1"
                                    component={'span'}
                                  >
                                    <strong>{'Country:'}</strong>{' '}
                                    {address.country}
                                  </Typography>
                                </Grid>
                                {address.start_date && address.end_date ? (
                                  <Grid item xs={12}>
                                    <Typography
                                      variant="body1"
                                      component={'span'}
                                    >
                                      <strong>{'Dates:'}</strong>{' '}
                                      {`${dayjs(address.start_date).format('MMM D')} - ${dayjs(address.end_date).format('MMM D')}`}
                                    </Typography>
                                  </Grid>
                                ) : (
                                  <Grid item xs={12}>
                                    <Typography
                                      variant="body1"
                                      component={'span'}
                                    >
                                      <strong>{'Timing:'}</strong>{' '}
                                      {'Year Round'}
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Grid>
                          </CardContent>
                          {canEdit && (
                            <CardActions>
                              <Grid container item justifyContent="flex-end">
                                <Grid item paddingRight>
                                  <Button
                                    variant="contained"
                                    size={'small'}
                                    onClick={() =>
                                      handleConfirmRemoveAddress(address)
                                    }
                                    disabled={isSaving}
                                    color={'error'}
                                    sx={{ fontWeight: 'bold' }}
                                  >
                                    <Delete
                                      sx={{ fontSize: 18, paddingRight: 1 }}
                                    />
                                    Remove
                                  </Button>
                                </Grid>
                                <Grid item>
                                  <Button
                                    variant="contained"
                                    size={'small'}
                                    onClick={() => handleEditAddress(address)}
                                    disabled={isSaving}
                                    color={'blue'}
                                    sx={{ fontWeight: 'bold' }}
                                  >
                                    <Edit
                                      sx={{ fontSize: 18, paddingRight: 1 }}
                                    />
                                    Edit
                                  </Button>
                                </Grid>
                              </Grid>
                            </CardActions>
                          )}
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Grid>
            )}
          </Grid>
          {editingAddress && (
            <Grid item xs={12} paddingTop={3}>
              <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
                <CardHeader
                  title={`Edit ${addressType === addressTypes.Seasonal ? 'Seasonal' : 'Primary'} Address`}
                  sx={{ p: 1 }}
                />
                <CardContent sx={{ p: 1 }}>
                  <Grid container>
                    <Grid item xs={12} md={6} padding>
                      <CountrySelector
                        value={country}
                        handleChange={(value) => handleCountryChange(value)}
                        size="small"
                        error={countryError}
                        errorMessage={countryError ? 'Country is required' : ''}
                      />
                    </Grid>
                    <Grid item xs={12} md={6} padding>
                      <TextField
                        variant="outlined"
                        size="small"
                        required
                        fullWidth
                        id="past-code"
                        label="Postal Code"
                        value={delayPostalCode}
                        error={postalCodeError}
                        helperText={
                          postalCodeError ? 'Postal Code is required' : ''
                        }
                        onChange={handlePostalCodeChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6} padding>
                      <TextField
                        label="Street"
                        value={address1}
                        onChange={(e) => handleAddress1Change(e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                        error={address1Error}
                        helperText={address1Error ? 'Street is required' : ''}
                      />
                    </Grid>
                    <Grid item xs={12} md={6} padding>
                      <TextField
                        label="Street 2"
                        value={address2}
                        onChange={(e) => setAddress2(e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6} padding>
                      <TextField
                        label="City"
                        value={city}
                        onChange={(e) => handleCityChange(e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                        error={cityError}
                        helperText={cityError ? 'City is required' : ''}
                      />
                    </Grid>

                    <Grid item xs={12} md={6} padding>
                      <StateSelector
                        value={state}
                        handleChange={handleStateChange}
                        countryId={country}
                        size="small"
                        error={stateError}
                        errorMessage={stateError ? 'State is required' : ''}
                        disabled={!country}
                      />
                    </Grid>
                    {addressType === addressTypes.Seasonal && (
                      <Grid item xs={12} md={6} padding>
                        <DateFilter
                          label="Start Date"
                          filterValue={startDate}
                          updateFilters={handleStartDateChange}
                          views={['month', 'day']}
                          error={startDateError}
                          errorMessage={
                            startDateError ? 'Start Date is required' : ''
                          }
                          required={true}
                        />
                      </Grid>
                    )}
                    {addressType === addressTypes.Seasonal && (
                      <Grid item xs={12} md={6} padding>
                        <DateFilter
                          label="End Date"
                          filterValue={endDate}
                          updateFilters={handleEndDateChange}
                          views={['month', 'day']}
                          error={endDateError}
                          errorMessage={
                            endDateError ? 'End Date is required' : ''
                          }
                          required={true}
                        />
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
                {canEdit && (
                  <CardActions>
                    <Grid item container xs={12} padding paddingTop={4}>
                      {editingAddress && (
                        <Grid item container justifyContent={'flex-end'}>
                          <Grid item padding>
                            <Button
                              variant="contained"
                              size={'small'}
                              onClick={resetAddress}
                              disabled={isSaving}
                              color={'warning'}
                              sx={{ marginRight: 1, fontWeight: 'bold' }}
                            >
                              <Cancel sx={{ fontSize: 18, paddingRight: 1 }} />
                              Cancel Edit
                            </Button>
                            <Button
                              variant="contained"
                              size={'small'}
                              onClick={handleSaveAddress}
                              disabled={isSaving}
                              color={'green'}
                              sx={{ fontWeight: 'bold' }}
                            >
                              <Save sx={{ fontSize: 18, paddingRight: 1 }} />
                              Save
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </CardActions>
                )}
              </Card>
            </Grid>
          )}
        </Grid>
        <ConfirmationDialog
          open={showConfirmationDialog}
          handleCancel={handleCancelRemoveAddress}
          handleOk={() => handleRemoveAddress()}
          title="Remove Address"
          message="Are you sure you want to remove this address?"
          confirmText="Remove"
          cancelText="Cancel"
        />
      </Grid>
    );
  }
}
